import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../../components/ui/accordion';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import {
  MessageCircle, KeyRound, Minus, Plus, CheckCircle2, Loader2,
  ArrowRight, XCircle, Sparkles, Sofa, Users, User, Play,
} from 'lucide-react';
import './nazakat-shararat.css';

const SLUG = 'nazakat-shararat';
const YT_ID = '0J2ZYNwik38';
const YT_IS_SHORTS = true; // Portrait 9:16 embed for YouTube Shorts
const WA_NUM_1 = '917870369666';
const WA_NUM_2 = '918252593656';

const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

// ── State enum ────────────────────────────────────────────────────────
const STATE = { HERO: 'hero', GATE: 'gate', PICK: 'pick', CHECKOUT: 'checkout' };

export default function NazakatShararatPage() {
  const navigate = useNavigate();
  const [event, setEvent]     = useState(null);
  const [step, setStep]       = useState(STATE.HERO);
  const [inviteCode, setInviteCode] = useState('');
  const [inventory, setInventory]   = useState(null);
  const [selections, setSelections] = useState({});  // sku_id -> {category, sku_id, qty}
  const [couponCode, setCouponCode] = useState('');
  const [couponPct,  setCouponPct]  = useState(0);
  const [partial, setPartial] = useState({ vip_couches: 0.10, tables: 0.20, entry: 0.25 });

  useEffect(() => {
    publicApi.sxeGet(SLUG)
      .then((r) => setEvent(r.data))
      .catch(() => toast.error('Could not load event.'));
  }, []);

  // ── Totals math (mirrors backend) ─────────────────────────────────
  const totals = useMemo(() => {
    if (!inventory) return { subtotal: 0, discount: 0, total: 0, pay_now: 0, pay_at_gate: 0, lines: [] };
    let subtotal = 0, payNow = 0;
    const lines = [];
    Object.values(selections).forEach((s) => {
      if (!s || !s.qty) return;
      let unit = null;
      let pct = 1;
      if (s.category === 'vip_couch') {
        const c = inventory.vip_couches.find((x) => x.id === s.sku_id);
        if (!c) return; unit = c.price; pct = partial.vip_couches;
      } else if (s.category === 'table') {
        const t = inventory.tables.find((x) => x.id === s.sku_id);
        if (!t) return; unit = t.price; pct = partial.tables;
      } else if (s.category === 'entry') {
        const e = inventory.entry.find((x) => x.id === s.sku_id);
        if (!e) return; unit = e.price; pct = partial.entry;
      }
      const lineTotal = unit * s.qty;
      const linePayNow = lineTotal * pct;
      subtotal += lineTotal;
      payNow   += linePayNow;
      lines.push({ ...s, unit, lineTotal, pct, linePayNow });
    });
    const disc = subtotal * (couponPct / 100);
    const total = Math.max(0, subtotal - disc);
    const payNowFinal = Math.max(0, payNow * (1 - couponPct / 100));
    return {
      subtotal, discount: disc, total,
      pay_now: payNowFinal,
      pay_at_gate: Math.max(0, total - payNowFinal),
      lines,
    };
  }, [selections, inventory, partial, couponPct]);

  const totalItems = useMemo(
    () => Object.values(selections).reduce((sum, s) => sum + (s?.qty || 0), 0),
    [selections],
  );

  // ── Invite gate ──
  const validateInvite = async (code) => {
    try {
      const r = await publicApi.sxeValidateInvite(SLUG, (code || inviteCode).trim());
      setInviteCode(r.data.code);
      setInventory(r.data.inventory);
      setPartial(r.data.inventory.partial_payment || partial);
      setStep(STATE.PICK);
      toast.success('Welcome. Please pick your table.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid code.');
    }
  };

  // ── Selection helpers ──
  const setQty = (category, sku_id, qty, max) => {
    const q = Math.max(0, Math.min(qty, max ?? qty));
    setSelections((prev) => {
      const next = { ...prev };
      if (q === 0) delete next[sku_id];
      else next[sku_id] = { category, sku_id, qty: q };
      return next;
    });
  };

  const toggleCouch = (id, status) => {
    if (status !== 'available') return;
    setSelections((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = { category: 'vip_couch', sku_id: id, qty: 1 };
      return next;
    });
  };

  // ── Coupon apply ──
  const applyCoupon = async () => {
    try {
      const r = await publicApi.sxeValidateCoupon(SLUG, couponCode.trim());
      setCouponPct(r.data.discount_percent);
      toast.success(`Coupon applied: ${r.data.discount_percent}% off`);
    } catch (e) {
      setCouponPct(0);
      toast.error(e?.response?.data?.detail || 'Invalid coupon.');
    }
  };

  if (!event) {
    return (
      <>
        <SxeSeoHead />
        <div className="naz-page min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#D4A843] animate-spin" />
        </div>
      </>
    );
  }

  return (
    <div className="naz-page" data-testid="nazakat-page">
      <SxeSeoHead event={event} />
      <HeroSection event={event} onGetPasses={() => setStep(STATE.GATE)} />

      <GateModal
        open={step === STATE.GATE}
        onOpenChange={(o) => !o && setStep(STATE.HERO)}
        inviteCode={inviteCode}
        setInviteCode={setInviteCode}
        onValidate={validateInvite}
        eventTitle={event.title}
      />

      <Dialog open={step === STATE.PICK} onOpenChange={(o) => !o && setStep(STATE.HERO)}>
        <DialogContent className="naz-modal max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="naz-serif text-3xl text-[#F1E2B8]">Pick your night</DialogTitle>
            <DialogDescription className="text-[#F2F2F2]/70">
              Full-cover night · Balance payable at the door
            </DialogDescription>
          </DialogHeader>

          {inventory && (
            <div className="space-y-6 pt-2" data-testid="naz-inventory-section">
              <VipCouchGrid
                couches={inventory.vip_couches}
                selections={selections}
                onToggle={toggleCouch}
                partialPct={partial.vip_couches}
              />
              <TableSection
                tables={inventory.tables}
                selections={selections}
                onQty={setQty}
                partialPct={partial.tables}
              />
              <EntrySection
                entries={inventory.entry}
                selections={selections}
                onQty={setQty}
                partialPct={partial.entry}
              />

              {totalItems > 0 && (
                <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-black/85 backdrop-blur border-t border-[#D4A843]/25">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[#F2F2F2]/60 text-[10px] uppercase tracking-widest">Pay now</p>
                      <p className="naz-serif text-2xl text-[#F1E2B8]" data-testid="naz-pay-now-summary">
                        {fmtINR(totals.pay_now)}
                      </p>
                      <p className="text-[10px] text-[#F2F2F2]/50">
                        + {fmtINR(totals.pay_at_gate)} at gate
                      </p>
                    </div>
                    <Button className="naz-btn-gold" onClick={() => setStep(STATE.CHECKOUT)}
                      data-testid="naz-proceed-checkout">
                      Continue <ArrowRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CheckoutSummary
        open={step === STATE.CHECKOUT}
        onClose={() => setStep(STATE.PICK)}
        totals={totals}
        selections={selections}
        inventory={inventory}
        inviteCode={inviteCode}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        couponPct={couponPct}
        applyCoupon={applyCoupon}
        onPaidRedirect={(token) => navigate(`/events/specialevents/${SLUG}/ticket/${token}`)}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SEO HEAD — On-page meta tags, Open Graph, Twitter Card, JSON-LD Event schema
// ═══════════════════════════════════════════════════════════════════════════
//
// This block controls how the page appears in:
//   1. Google Search results (title, description, rich Event snippet)
//   2. WhatsApp / iMessage / Slack link previews (og:image + og:title)
//   3. Twitter / X link cards
//   4. Facebook + LinkedIn feed cards
//
// We tailor the SEO for the *canonical* production domain so search engines
// index the right URL even when the site is browsed via preview hostnames.
// ═══════════════════════════════════════════════════════════════════════════
const SEO_CANONICAL_ORIGIN = 'https://scratchjsr.com';
const SEO_CANONICAL_PATH   = '/events/specialevents/nazakat-shararat';
const SEO_CANONICAL_URL    = `${SEO_CANONICAL_ORIGIN}${SEO_CANONICAL_PATH}`;

function SxeSeoHead({ event }) {
  // Fallback strings so meta tags are populated even before /public payload lands.
  const title       = event?.title       || 'Nazakat & Shararat';
  const tagline     = event?.tagline     || 'An invite-only night of imperfect elegance.';
  const venue       = event?.venue       || 'Scratch Unleashed, Jamshedpur';
  const eventDate   = event?.event_date  || '2026-07-18T20:00:00+05:30';
  const posterPath  = event?.poster_url  || '/naz/poster.jpg';

  // Absolute URLs are mandatory for Open Graph / Twitter / JSON-LD crawlers.
  const posterAbsolute = posterPath.startsWith('http')
    ? posterPath
    : `${SEO_CANONICAL_ORIGIN}${posterPath}`;

  const pageTitle = `${title} · Invite-Only Night · Scratch Unleashed Jamshedpur`;
  const pageDesc  = `${tagline} Live performance by Ayesha Khan · Sat 18 July · ${venue}. Book VIP couches, tables and entry passes with partial payment.`;
  const keywordsList = 'nazakat and shararat, ayesha khan, jamshedpur live music, scratch unleashed, ghazal night, invite only event, jharkhand nightlife, vip couch booking, ticket booking jamshedpur';

  // Price entry-point (entry-pass floor per current inventory). Helps Google
  // Event rich result display a "from ₹1,500" badge.
  const lowestPrice = 1500;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type':    'MusicEvent',
    name:       title,
    description: `${tagline} Live performance by Ayesha Khan.`,
    startDate:  eventDate,
    endDate:    (() => {
      try {
        const d = new Date(eventDate);
        d.setHours(d.getHours() + 5);
        return d.toISOString();
      } catch (_e) { return eventDate; }
    })(),
    eventStatus:            'https://schema.org/EventScheduled',
    eventAttendanceMode:    'https://schema.org/OfflineEventAttendanceMode',
    image:                  [posterAbsolute],
    url:                    SEO_CANONICAL_URL,
    location: {
      '@type':  'Place',
      name:     'Scratch Unleashed',
      address: {
        '@type':          'PostalAddress',
        streetAddress:    'Sakchi',
        addressLocality:  'Jamshedpur',
        addressRegion:    'Jharkhand',
        postalCode:       '831001',
        addressCountry:   'IN',
      },
    },
    performer: {
      '@type': 'PerformingGroup',
      name:    (event?.headliner?.name) || 'Ayesha Khan',
    },
    organizer: {
      '@type': 'Organization',
      name:    'Scratch Unleashed',
      url:     SEO_CANONICAL_ORIGIN,
    },
    offers: {
      '@type':        'Offer',
      url:            SEO_CANONICAL_URL,
      price:          lowestPrice,
      priceCurrency:  'INR',
      availability:   'https://schema.org/InStock',
      validFrom:      new Date().toISOString(),
      category:       'primary',
    },
    isAccessibleForFree: false,
    inLanguage:          'en-IN',
    keywords: [
      'nazakat and shararat', 'ayesha khan live jamshedpur', 'scratch unleashed',
      'jamshedpur nightlife', 'ghazal night jamshedpur', 'invite only event jharkhand',
      'live music jamshedpur', 'vip couch booking',
    ].join(', '),
  };

  // ── Overwrite existing static site-wide meta tags in place ──────────────
  // We MUTATE the pre-baked meta tags from index.html rather than injecting
  // duplicates. This is safer than removing DOM nodes (which crashed React's
  // reconciler) and cleaner for Google, which flags duplicate meta tags as
  // a low-quality signal. Snapshots are restored on unmount so other routes
  // keep their site-wide defaults.
  useEffect(() => {
    const overrides = [
      ['meta[name="description"]',        'content',  pageDesc],
      ['meta[name="keywords"]',           'content',  keywordsList],
      ['meta[name="robots"]',             'content',  'index, follow, max-image-preview:large'],
      ['meta[property="og:type"]',        'content',  'event'],
      ['meta[property="og:site_name"]',   'content',  'Scratch Unleashed'],
      ['meta[property="og:title"]',       'content',  pageTitle],
      ['meta[property="og:description"]', 'content',  pageDesc],
      ['meta[property="og:image"]',       'content',  posterAbsolute],
      ['meta[property="og:image:alt"]',   'content',  `${title} — Poster featuring ${(event?.headliner?.name) || 'Ayesha Khan'}`],
      ['meta[property="og:image:width"]', 'content',  '1200'],
      ['meta[property="og:image:height"]','content',  '630'],
      ['meta[property="og:url"]',         'content',  SEO_CANONICAL_URL],
      ['meta[property="og:locale"]',      'content',  'en_IN'],
      ['meta[name="twitter:card"]',       'content',  'summary_large_image'],
      ['meta[name="twitter:title"]',      'content',  pageTitle],
      ['meta[name="twitter:description"]','content',  pageDesc],
      ['meta[name="twitter:image"]',      'content',  posterAbsolute],
      ['link[rel="canonical"]',           'href',     SEO_CANONICAL_URL],
    ];
    const snapshots = [];
    overrides.forEach(([sel, attr, value]) => {
      let el = document.head.querySelector(sel);
      if (el) {
        snapshots.push({ el, attr, prev: el.getAttribute(attr) });
        el.setAttribute(attr, value);
      } else {
        // The static HTML doesn't have this tag — create it so crawlers
        // see it, and mark for removal on cleanup.
        el = document.createElement(sel.startsWith('link') ? 'link' : 'meta');
        // Parse the selector to derive the identifier attribute + value
        // (works for our simple `tag[attr="value"]` selectors).
        const identMatch = sel.match(/\[(\w+)="([^"]+)"\]/);
        if (identMatch) el.setAttribute(identMatch[1], identMatch[2]);
        if (sel.startsWith('link[rel="canonical"]')) el.setAttribute('rel', 'canonical');
        el.setAttribute(attr, value);
        el.setAttribute('data-sxe-seo-added', 'true');
        document.head.appendChild(el);
        snapshots.push({ el, attr, added: true });
      }
    });

    // Replace any existing JSON-LD block with our Event schema, and mark our
    // node so cleanup can find it precisely.
    const existingLd = document.head.querySelector('script[type="application/ld+json"]:not([data-sxe-seo])');
    let ldSnapshot = null;
    if (existingLd) {
      ldSnapshot = { el: existingLd, prev: existingLd.textContent };
      existingLd.textContent = JSON.stringify(jsonLd);
      existingLd.setAttribute('data-sxe-override', 'true');
    } else {
      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.dataset.sxeSeo = 'true';
      ld.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ld);
      ldSnapshot = { added: ld };
    }

    return () => {
      snapshots.forEach((snap) => {
        if (snap.added) {
          try { snap.el.parentNode?.removeChild(snap.el); } catch (_e) { /* ignore */ }
          return;
        }
        const { el, attr, prev } = snap;
        if (prev === null) el.removeAttribute(attr);
        else               el.setAttribute(attr, prev);
      });
      if (ldSnapshot?.added) {
        try { ldSnapshot.added.parentNode?.removeChild(ldSnapshot.added); } catch (_e) { /* ignore */ }
      } else if (ldSnapshot?.el) {
        ldSnapshot.el.textContent = ldSnapshot.prev;
        ldSnapshot.el.removeAttribute('data-sxe-override');
      }
    };
  }, [pageTitle, pageDesc, posterAbsolute, eventDate, jsonLd, keywordsList]);

  return (
    <Helmet>
      {/* Only the <title> is Helmet-managed; the rest are mutated in place
          via the useEffect above so we don't create duplicate meta tags. */}
      <title>{pageTitle}</title>
    </Helmet>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════════════════
function HeroSection({ event, onGetPasses }) {
  const eventDate = new Date(event.event_date);
  const cd = useCountdown(eventDate);

  return (
    <>
      <section className="naz-hero" data-testid="naz-hero">
        {/* Subtle texture layer — poster reduced to ~5% for premium grain */}
        <div
          className="naz-hero-bg"
          aria-hidden="true"
          style={event.poster_url ? {
            backgroundImage: `url("${event.poster_url}")`,
          } : undefined}
        />
        <div className="naz-hero-vignette" aria-hidden="true" />
        <div className="naz-hero-glow" aria-hidden="true" />

        <div className="naz-hero-inner">
          <p className="naz-eyebrow" data-testid="naz-event-meta">
            Sat · 18 July · 8PM+ · {event.venue}
          </p>
          <h1 className="naz-title" data-testid="naz-title">
            Nazakat<br /><span className="naz-and">&amp;</span> Shararat
          </h1>
          <p className="naz-tagline">{event.tagline}</p>

          <div className="naz-countdown" data-testid="naz-countdown" aria-label="Time until event starts">
            {[['DAYS', cd.d], ['HRS', cd.h], ['MIN', cd.m], ['SEC', cd.s]].map(([lbl, val]) => (
              <div key={lbl} className="naz-cd-cell">
                <span className="naz-cd-val tabular-nums">{String(val).padStart(2, '0')}</span>
                <span className="naz-cd-lbl">{lbl}</span>
              </div>
            ))}
          </div>

          {/* YouTube preview — click to play */}
          <YouTubePreview
            ytId={YT_ID}
            isShorts={YT_IS_SHORTS}
            posterUrl={event.poster_url}
            title="Nazakat & Shararat — Teaser"
          />
        </div>
      </section>

      {/* ── ARTIST SECTION ── */}
      {event.headliner?.name && (
        <section className="naz-artist" data-testid="naz-performer-reveal">
          <div className="naz-artist-inner">
            <p className="naz-performer-tag">Performing Live</p>
            <h2 className="naz-performer-name">{event.headliner.name}</h2>
            <p className="naz-performer-bio">{event.headliner.bio}</p>
          </div>
        </section>
      )}

      {/* ── HOSTS / MUSIC BY ── */}
      <section className="naz-credits">
        <div className="naz-credits-inner">
          <div className="naz-credit-block">
            <p className="naz-eyebrow">Music by</p>
            <p className="naz-hosts-line">{(event.music_by || []).join('  ·  ')}</p>
          </div>
          <div className="naz-credit-block">
            <p className="naz-eyebrow">Hosted by</p>
            <p className="naz-hosts-line">{(event.hosts || []).join('  ·  ')}</p>
          </div>
        </div>
      </section>

      <div className="naz-sticky-cta" data-testid="naz-sticky-cta-bar">
        <div className="naz-sticky-inner">
          <div className="naz-sticky-text">
            <p className="naz-eyebrow">Invite only · from ₹1,500</p>
            <p className="naz-sticky-heading">Reserve your night</p>
          </div>
          <Button className="naz-btn-gold" onClick={onGetPasses} data-testid="naz-get-passes-button">
            <Sparkles size={14} className="mr-2" /> Get Passes
          </Button>
        </div>
      </div>
    </>
  );
}

// ── YouTube preview: shows branded poster + play, swaps to iframe on click ──
function YouTubePreview({ ytId, isShorts, posterUrl, title }) {
  const [playing, setPlaying] = useState(false);
  if (!ytId) return null;
  // Shorts use portrait embed via /embed/{id}. Minimal params for widest compatibility.
  const embedSrc = `https://www.youtube.com/embed/${ytId}?autoplay=1&playsinline=1&rel=0`;
  // YouTube's own thumbnail for Shorts renders portrait; fallback to poster.
  const ytThumb = `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`;
  const thumbBg = posterUrl || ytThumb;
  return (
    <div
      className={`naz-yt-card ${isShorts ? 'is-shorts' : ''}`}
      data-testid="naz-hero-youtube-preview"
    >
      {playing ? (
        <iframe
          className="naz-yt-iframe"
          src={embedSrc}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          data-testid="naz-hero-youtube-iframe"
        />
      ) : (
        <button
          type="button"
          className="naz-yt-thumb"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${title}`}
          data-testid="naz-hero-youtube-play-button"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.6) 100%), url("${thumbBg}")`,
          }}
        >
          <span className="naz-yt-play">
            <Play size={22} fill="currentColor" strokeWidth={0} />
          </span>
          <span className="naz-yt-label">Watch teaser</span>
        </button>
      )}
    </div>
  );
}

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s };
}

// ═══════════════════════════════════════════════════════════════════════════
// GATE MODAL
// ═══════════════════════════════════════════════════════════════════════════
function GateModal({ open, onOpenChange, inviteCode, setInviteCode, onValidate }) {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    await onValidate();
    setBusy(false);
  };

  const waLink = (num) => {
    const text = encodeURIComponent(
      `Hi, I'd like to book passes for Nazakat & Shararat (Sat 18 July). Please share an invite code — we are ___ couples and ___ female stag.`,
    );
    return `https://wa.me/${num}?text=${text}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="naz-modal max-w-md" data-testid="naz-invite-gate-modal">
        <DialogHeader>
          <DialogTitle className="naz-serif text-3xl text-[#F1E2B8]">Invite Only</DialogTitle>
          <DialogDescription className="text-[#F2F2F2]/70 text-sm">
            This night runs on invitations. Message us on WhatsApp to receive a code, then paste it below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <a href={waLink(WA_NUM_1)} target="_blank" rel="noreferrer">
            <Button variant="outline" className="w-full naz-wa-btn" data-testid="naz-whatsapp-cta-1">
              <MessageCircle size={16} className="mr-2 text-emerald-400" />
              WhatsApp · +91 78703 69666
            </Button>
          </a>
          <a href={waLink(WA_NUM_2)} target="_blank" rel="noreferrer">
            <Button variant="outline" className="w-full naz-wa-btn" data-testid="naz-whatsapp-cta-2">
              <MessageCircle size={16} className="mr-2 text-emerald-400" />
              WhatsApp · +91 82525 93656
            </Button>
          </a>

          <div className="flex items-center gap-3 my-1">
            <span className="flex-1 h-px bg-[#D4A843]/25" />
            <span className="text-[10px] uppercase tracking-widest text-[#F2F2F2]/50">Have a code?</span>
            <span className="flex-1 h-px bg-[#D4A843]/25" />
          </div>

          <form onSubmit={submit} className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest text-[#F2F2F2]/70">Invite code</Label>
            <div className="relative">
              <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4A843]" />
              <Input
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter your invite code"
                className="naz-input pl-9 tracking-wider"
                data-testid="naz-invite-code-input"
                autoFocus
              />
            </div>
            <p className="text-[10px] text-[#F2F2F2]/50">Codes are case-sensitive.</p>
            <Button type="submit" className="naz-btn-gold w-full" disabled={busy || !inviteCode.trim()}
              data-testid="naz-invite-code-validate-button">
              {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
              Unlock inventory
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// INVENTORY SECTIONS
// ═══════════════════════════════════════════════════════════════════════════
function VipCouchGrid({ couches, selections, onToggle, partialPct }) {
  return (
    <div data-testid="naz-vip-couch-grid">
      <SectionHeading icon={Sofa} title="VIP Couches" subtitle={`Pay ${(partialPct*100).toFixed(0)}% now · Rest at gate · 6–8 pax`} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {couches.map((c) => {
          const isSel = !!selections[c.id];
          const sold  = c.status !== 'available';
          return (
            <button
              key={c.id}
              type="button"
              disabled={sold}
              onClick={() => onToggle(c.id, c.status)}
              className={`naz-couch-tile ${isSel ? 'is-selected' : ''} ${sold ? 'is-sold' : ''}`}
              data-testid={`naz-couch-${c.id}`}
            >
              <span className="naz-couch-id">{c.id}</span>
              <span className="naz-couch-price">{fmtINR(c.price)}</span>
              {sold && <span className="naz-couch-sold">Sold</span>}
              {isSel && <CheckCircle2 size={14} className="text-[#F1E2B8] absolute top-1.5 right-1.5" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TableSection({ tables, selections, onQty, partialPct }) {
  return (
    <div data-testid="naz-regular-table-options">
      <SectionHeading icon={Users} title="Regular Tables" subtitle={`Pay ${(partialPct*100).toFixed(0)}% now · Rest at gate`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {tables.map((t) => (
          <QtyCard
            key={t.id}
            title={t.label}
            price={t.price}
            subtitle={`For ${t.capacity} · ${t.available} left`}
            available={t.available}
            qty={selections[t.id]?.qty || 0}
            onChange={(q) => onQty('table', t.id, q, t.available)}
            testid={`naz-table-${t.id}`}
          />
        ))}
      </div>
    </div>
  );
}

function EntrySection({ entries, selections, onQty, partialPct }) {
  return (
    <div data-testid="naz-entry-options">
      <SectionHeading icon={User} title="Entry Passes" subtitle={`Pay ${(partialPct*100).toFixed(0)}% now · Full cover night`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {entries.map((e) => (
          <QtyCard
            key={e.id}
            title={e.label}
            price={e.price}
            subtitle={`${e.available} left`}
            available={e.available}
            qty={selections[e.id]?.qty || 0}
            onChange={(q) => onQty('entry', e.id, q, e.available)}
            testid={`naz-entry-${e.id}`}
          />
        ))}
      </div>
    </div>
  );
}

function SectionHeading({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-[#D4A843]" />
        <h3 className="naz-serif text-lg text-[#F1E2B8]">{title}</h3>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-[#F2F2F2]/50 mt-0.5">{subtitle}</p>
    </div>
  );
}

function QtyCard({ title, price, subtitle, qty, onChange, available, testid }) {
  const disabledPlus = available !== undefined && qty >= available;
  return (
    <div className={`naz-qty-card ${qty > 0 ? 'is-selected' : ''}`} data-testid={testid}>
      <div className="flex-1">
        <p className="text-[#F2F2F2] text-sm font-medium">{title}</p>
        <p className="text-[10px] text-[#F2F2F2]/50">{subtitle}</p>
        <p className="text-[#D4A843] text-sm font-semibold mt-1">{fmtINR(price)}</p>
      </div>
      <div className="naz-stepper">
        <button type="button" onClick={() => onChange(qty - 1)} disabled={qty <= 0} aria-label="Decrease">
          <Minus size={13} />
        </button>
        <span className="tabular-nums w-6 text-center text-sm">{qty}</span>
        <button type="button" onClick={() => onChange(qty + 1)} disabled={disabledPlus} aria-label="Increase">
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CHECKOUT
// ═══════════════════════════════════════════════════════════════════════════
function CheckoutSummary({
  open, onClose, totals, selections, inventory, inviteCode,
  couponCode, setCouponCode, couponPct, applyCoupon, onPaidRedirect,
}) {
  const [name, setName]   = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp]     = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setInterval(() => setOtpCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [otpCooldown]);

  const sendOtp = async () => {
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      toast.error('Enter a valid mobile number.'); return;
    }
    try {
      setBusy(true);
      await publicApi.sendOTPSmart(phone);
      setOtpSent(true);
      setOtpCooldown(30);
      toast.success('OTP sent.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send OTP.');
    } finally { setBusy(false); }
  };

  const finalize = async () => {
    if (!name.trim() || !phone || !otp || otp.length !== 6) {
      toast.error('Please complete name, phone and OTP.'); return;
    }
    setBusy(true);
    try {
      const items = Object.values(selections).map((s) => ({
        category: s.category, sku_id: s.sku_id, qty: s.qty,
      }));
      const res = await publicApi.sxeCreateBooking(SLUG, {
        invite_code: inviteCode,
        coupon_code: couponCode.trim() || null,
        full_name:   name.trim(),
        phone,
        email:       email.trim() || null,
        otp:         otp.trim(),
        items,
      });
      // Auto-POST to Paytm
      const { gateway_url, form_fields } = res.data;
      setAutoSubmitting(true);
      const form = formRef.current;
      form.innerHTML = '';
      form.action = gateway_url;
      form.method = 'POST';
      Object.entries(form_fields || {}).forEach(([k, v]) => {
        const inp = document.createElement('input');
        inp.type = 'hidden'; inp.name = k; inp.value = v;
        form.appendChild(inp);
      });
      // Small delay so the loader is visible
      setTimeout(() => form.submit(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Booking failed.');
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="naz-modal max-w-md max-h-[92vh] overflow-y-auto"
          data-testid="naz-checkout-summary">
          <DialogHeader>
            <DialogTitle className="naz-serif text-3xl text-[#F1E2B8]">Almost there</DialogTitle>
            <DialogDescription className="text-[#F2F2F2]/70 text-sm">
              Verify your number to secure the reservation.
            </DialogDescription>
          </DialogHeader>

          {autoSubmitting ? (
            <div className="py-10 text-center">
              <Loader2 className="w-8 h-8 text-[#D4A843] animate-spin mx-auto mb-3" />
              <p className="text-[#F2F2F2]/70 text-sm">Opening Paytm…</p>
            </div>
          ) : (
            <>
              {/* Line items */}
              <div className="space-y-1.5 text-sm mb-4">
                {totals.lines.map((l) => {
                  const label = resolveLabel(inventory, l);
                  return (
                    <div key={l.sku_id} className="flex justify-between text-[#F2F2F2]/80">
                      <span>{label} × {l.qty}</span>
                      <span className="tabular-nums">{fmtINR(l.lineTotal)}</span>
                    </div>
                  );
                })}
                <div className="pt-2 mt-2 border-t border-[#D4A843]/20 flex justify-between text-[#F2F2F2]/70">
                  <span>Subtotal</span>
                  <span className="tabular-nums">{fmtINR(totals.subtotal)}</span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-emerald-300">
                    <span>Discount ({couponPct}%)</span>
                    <span className="tabular-nums">− {fmtINR(totals.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#F1E2B8] font-semibold">
                  <span>Total</span>
                  <span className="tabular-nums">{fmtINR(totals.total)}</span>
                </div>
                <div className="mt-3 p-2.5 rounded bg-[#D4A843]/8 border border-[#D4A843]/25 text-xs">
                  <div className="flex justify-between">
                    <span className="text-[#F1E2B8]">Pay now</span>
                    <span className="naz-serif text-lg" data-testid="naz-pay-now-amount">{fmtINR(totals.pay_now)}</span>
                  </div>
                  <div className="flex justify-between text-[#F2F2F2]/70 mt-0.5">
                    <span>Balance at gate</span>
                    <span data-testid="naz-pay-at-gate-amount">{fmtINR(totals.pay_at_gate)}</span>
                  </div>
                </div>
              </div>

              <Accordion type="single" collapsible className="mb-3">
                <AccordionItem value="coupon" className="border-[#D4A843]/20">
                  <AccordionTrigger className="text-xs uppercase tracking-widest text-[#F2F2F2]/60 py-2"
                    data-testid="naz-coupon-accordion-trigger">
                    Have a code?
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex gap-2 pt-1">
                      <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value)}
                        placeholder="Coupon" className="naz-input"
                        data-testid="naz-coupon-input" />
                      <Button type="button" onClick={applyCoupon} className="naz-btn-gold"
                        disabled={!couponCode.trim()} data-testid="naz-apply-coupon-button">
                        Apply
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Customer info */}
              <div className="space-y-2.5">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#F2F2F2]/70">Full name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                    className="naz-input" data-testid="naz-name-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#F2F2F2]/70">Mobile</Label>
                  <div className="flex gap-2">
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91 98xxxxxxxx"
                      className="naz-input flex-1" data-testid="naz-phone-input" />
                    <Button type="button" variant="outline" onClick={sendOtp}
                      disabled={busy || otpCooldown > 0} className="naz-wa-btn text-xs px-3"
                      data-testid="naz-send-otp-button">
                      {otpCooldown > 0 ? `${otpCooldown}s` : (otpSent ? 'Resend' : 'Send OTP')}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#F2F2F2]/70">Email (optional)</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)}
                    type="email" className="naz-input" data-testid="naz-email-input" />
                </div>
                {otpSent && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#F2F2F2]/70">6-digit OTP</Label>
                    <InputOTP maxLength={6} value={otp} onChange={setOtp}
                      containerClassName="naz-otp" data-testid="naz-otp-input">
                      <InputOTPGroup>
                        {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                )}
                <Button className="naz-btn-gold w-full mt-2" onClick={finalize}
                  disabled={busy || !otpSent || otp.length !== 6}
                  data-testid="naz-paytm-checkout-button">
                  {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                  Pay {fmtINR(totals.pay_now)} · Continue
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <form ref={formRef} style={{ display: 'none' }} />
    </>
  );
}

function resolveLabel(inv, line) {
  if (!inv) return line.sku_id;
  if (line.category === 'vip_couch')
    return (inv.vip_couches.find(c => c.id === line.sku_id) || {}).label || line.sku_id;
  if (line.category === 'table')
    return (inv.tables.find(t => t.id === line.sku_id) || {}).label || line.sku_id;
  if (line.category === 'entry')
    return (inv.entry.find(e => e.id === line.sku_id) || {}).label || line.sku_id;
  return line.sku_id;
}
